// contexts/UserContext.js
import React, {
  createContext, useContext, useState, useEffect,
  useMemo,
} from 'react';
import { useQuery } from '@apollo/client';
import GET_CURRENT_USER from '../gql/queries/get-useraccount-info.gql';

// Create the context
const UserContext = createContext();

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// User Provider component
export function UserProvider({ children, initialUser, userToken }) {
  const [user, setUser] = useState(initialUser);
  const [isLoading, setIsLoading] = useState(false);
  // Query to refetch user data on client-side if needed
  const {
    data, loading, error, refetch,
  } = useQuery(GET_CURRENT_USER, {
    skip: !userToken, // Skip if no token
    errorPolicy: 'ignore',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      // console.log('User data fetched:', data);
      if (data) {
        setUser(data?.me);
      }
    },
    onError: (error) => {
      console.error('Error fetching user:', error);
      setUser(null);
    },
  });

  // Update loading state
  useEffect(() => {
    setIsLoading(loading);
  }, [loading]);

  // Function to update user data
  const updateUser = (userData) => {
    setUser(userData);
  };

  // Function to clear user data (for logout)
  const clearUser = () => {
    setUser(null);
  };

  // Function to refresh user data
  const refreshUser = async () => {
    if (userToken && refetch) {
      try {
        setIsLoading(true);
        const { data } = await refetch();
        if (data) {
          setUser(data);
        }
      } catch (error) {
        console.error('Error refreshing user:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Check if user is authenticated
  const isAuthenticated = !!(user && userToken);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isLoading,
    error,
    updateUser,
    clearUser,
    refreshUser,
    userToken,
  }), [user, isAuthenticated, isLoading, error, userToken]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export default UserContext;
